import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookMarked, Upload, Loader2, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

export default function ReferenceImportPanel({ userId, projectId }) {
  const [refs, setRefs] = useState([]);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    try {
      setRefs(await thesisApi.listReferences(userId, projectId));
    } catch (e) {
      console.error(e);
    }
  }, [userId, projectId]);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const res = await thesisApi.importReferences(userId, projectId, file);
      toast.success(`Imported ${res.imported} references${res.skipped_duplicates ? ` · ${res.skipped_duplicates} duplicates skipped` : ''}`);
      load();
    } catch (err) {
      toast.error('Import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await thesisApi.deleteReference(userId, id);
      load();
    } catch (e) {
      toast.error('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <Card data-testid="reference-import-panel">
      <CardHeader>
        <CardTitle className="font-serif flex items-center gap-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <BookMarked className="w-5 h-5 text-[#1d4ed8]" /> Reference import
          <Badge variant="outline" className="text-[10px]">RIS · BibTeX · EndNote</Badge>
        </CardTitle>
        <CardDescription>
          Import your library from Zotero, Mendeley, EndNote or PubMed (export as .ris, .bib or .enw).
          Imported entries feed the numbered bibliography — paste their citation token into any section.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="cursor-pointer block">
          <input type="file" accept=".ris,.bib,.bibtex,.enw,.txt" className="hidden"
            onChange={handleImport} disabled={importing} data-testid="reference-import-input" />
          <Button asChild disabled={importing} variant="outline" className="w-full">
            <span>{importing
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</>
              : <><Upload className="w-4 h-4 mr-2" />Import .ris / .bib / .enw</>}</span>
          </Button>
        </label>

        {refs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4" data-testid="references-empty">
            No imported references yet.
          </p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="references-list">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
              {refs.length} references
            </div>
            {refs.map((r) => (
              <div key={r.id} className="p-2.5 rounded-lg border border-border text-sm"
                data-testid={`reference-row-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium leading-snug">{r.title || '(untitled)'}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {(r.authors || []).slice(0, 3).join(', ')}{(r.authors || []).length > 3 && ' et al.'}
                      {r.journal ? ` · ${r.journal}` : ''}{r.year ? ` · ${r.year}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost"
                      onClick={() => { navigator.clipboard.writeText(r.token); toast.success(`Copied ${r.token}`); }}
                      data-testid={`reference-copy-${r.id}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)}
                      data-testid={`reference-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
                <code className="text-[10px] text-[#1d4ed8] dark:text-[#7aa9ff]">{r.token}</code>
                {r.index_note && (
                  <span className={`ml-2 text-[10px] ${r.indexed ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                    · {r.index_note}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
